import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, List, X } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/materiel', label: 'Matériel de bureau' },
    { path: '/voitures', label: 'Voitures' },
    { path: '/contact', label: 'Contact' },
    { path: '/a-propos', label: 'À propos' },
];

export default function Header() {
    const { theme, toggleTheme } = useTheme();
    const { config } = useSiteConfig();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    const LOGO_GLOBAL = config.logo_global;
    const LOGO_LUXURY = config.logo_luxury;

    return (
        <header 
            data-testid="main-header"
            className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        >
            <div className={`${theme === 'dark' ? 'glass-dark' : 'glass-light'} border-b`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        {/* Left Logo - MMD Global */}
                        <Link to="/" className="flex items-center gap-3" data-testid="logo-mmd-global">
                            <img 
                                src={LOGO_GLOBAL} 
                                alt="MMD Global" 
                                className="h-12 w-12 rounded-full object-cover"
                            />
                            <span className="hidden sm:block font-heading font-semibold text-lg tracking-tight">
                                MMD GLOBAL
                            </span>
                        </Link>

                        {/* Center Navigation - Desktop */}
                        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        location.pathname === link.path
                                            ? theme === 'dark' 
                                                ? 'bg-mmd-gold/20 text-mmd-gold' 
                                                : 'bg-mmd-navy/10 text-mmd-navy'
                                            : 'hover:bg-foreground/5'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Right Side */}
                        <div className="flex items-center gap-4">
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                data-testid="theme-toggle"
                                className={`p-2.5 rounded-lg transition-all duration-200 ${
                                    theme === 'dark' 
                                        ? 'bg-mmd-gold/20 text-mmd-gold hover:bg-mmd-gold/30' 
                                        : 'bg-mmd-navy/10 text-mmd-navy hover:bg-mmd-navy/20'
                                }`}
                                aria-label="Toggle theme"
                            >
                                {theme === 'dark' ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
                            </button>

                            {/* Right Logo - MMD Luxury Cars */}
                            <Link to="/voitures" className="flex items-center gap-3" data-testid="logo-mmd-luxury">
                                <span className="hidden sm:block font-heading font-semibold text-lg tracking-tight">
                                    MMD LUXURY CAR
                                </span>
                                <img 
                                    src={LOGO_LUXURY} 
                                    alt="MMD Luxury Cars" 
                                    className="h-12 w-12 rounded-full object-cover"
                                />
                            </Link>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                data-testid="mobile-menu-toggle"
                                className="lg:hidden p-2 rounded-lg hover:bg-foreground/5"
                                aria-label="Toggle mobile menu"
                            >
                                {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`lg:hidden ${theme === 'dark' ? 'glass-dark' : 'glass-light'} border-b`}
                        data-testid="mobile-menu"
                    >
                        <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        location.pathname === link.path
                                            ? theme === 'dark' 
                                                ? 'bg-mmd-gold/20 text-mmd-gold' 
                                                : 'bg-mmd-navy/10 text-mmd-navy'
                                            : 'hover:bg-foreground/5'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
